var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { InvoiceRunItemStatus } from '../enums/invoice-run-item-status.enum.js';
import { InvoiceRunScopeMode } from '../enums/invoice-run-scope-mode.enum.js';
import { InvoiceRunStatus } from '../enums/invoice-run-status.enum.js';
/**
 * One `InvoiceRunItem` row inside the status response. Each item represents
 * one planned invoice (one consolidation group at command time).
 *
 * On `SUCCESS`, `patientInvoiceId` carries the assembled invoice. When the
 * run had `alsoIssue` enabled, `issued` indicates whether the issue step
 * succeeded; if it failed, the invoice falls back to a draft and
 * `issueFailureMessage` carries the reason.
 *
 * On `SKIPPED`, `skipReason` is set; on `FAILED`, `errorMessage` is set.
 */
export class InvoiceRunItemStatusDto {
    id;
    patientId;
    /**
     * The treatment-plan ids whose units were planned to be billed by this
     * item. Slice 1: at least one. Multi-plan groups appear once `consolidate`
     * is enabled (slice 4).
     */
    treatmentPlanIds;
    payerDomainCode;
    currency;
    /** Number of treatment units planned to be billed (snapshot at command time). */
    plannedUnitCount;
    status;
    /** Set when status === SUCCESS — the assembled invoice (draft or issued). */
    patientInvoiceId;
    /**
     * `true` when the run had `alsoIssue` enabled AND the issue step succeeded.
     * `false` otherwise — either because the run was draft-only or because the
     * issue step failed and the invoice fell back to a draft (see
     * `issueFailureMessage`).
     */
    issued;
    /**
     * Set when the run had `alsoIssue` enabled and the issue step failed after
     * the draft was already persisted. Always `null` for draft-only runs and
     * for items that were successfully issued.
     */
    issueFailureMessage;
    /** Set when status === SKIPPED. */
    skipReason;
    /** Set when status === FAILED. */
    errorMessage;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunItemStatusDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunItemStatusDto.prototype, "patientId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], InvoiceRunItemStatusDto.prototype, "treatmentPlanIds", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunItemStatusDto.prototype, "payerDomainCode", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunItemStatusDto.prototype, "currency", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunItemStatusDto.prototype, "plannedUnitCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunItemStatusDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunItemStatusDto.prototype, "patientInvoiceId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], InvoiceRunItemStatusDto.prototype, "issued", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunItemStatusDto.prototype, "issueFailureMessage", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunItemStatusDto.prototype, "skipReason", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunItemStatusDto.prototype, "errorMessage", void 0);
/**
 * Response of `GET /v1/invoice-run/:id` — drives the Running / Completed
 * views. Polled at ~1.5s while `status` is PENDING / IN_PROGRESS; polling
 * stops on terminal state.
 */
export class InvoiceRunStatusResponseDto {
    id;
    officeLocationId;
    status;
    /**
     * Run-level config snapshot: whether the run was asked to also issue each
     * assembled draft. Drives the ZIP download affordance in the completed
     * view (drafts have no PDF).
     */
    alsoIssue;
    /**
     * Slice 4 config snapshot — ISO-8601 cutoff applied by the reader. `null`
     * means "no cutoff" / all delivered. Surfaced for the audit trail so the
     * completed-run view can show which delivery date the operator chose.
     */
    deliveredUpTo;
    /**
     * Slice 4 config snapshot — whether the run merged each patient's
     * compatible plans into a single invoice.
     */
    consolidate;
    /**
     * Slice 5 config snapshot — which treatment plans the run swept.
     * `ALL_DELIVERED` (default) = every eligible delivered unit. `COMPLETED_ONLY`
     * = only plans with `status == COMPLETED`. Release-coupled to TM-9102.
     */
    scopeMode;
    /** ISO timestamp; null while still PENDING. */
    startedAt;
    /** ISO timestamp; null until COMPLETED / CANCELED / FAILED. */
    completedAt;
    totalCount;
    successCount;
    skippedCount;
    failedCount;
    pendingCount;
    /**
     * Number of items where `status === SUCCESS && issued === true`. Drives
     * "X als PDF ausgestellt" headline + enables the ZIP download. Always 0
     * when the run was draft-only.
     */
    issuedCount;
    items;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunStatusResponseDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunStatusResponseDto.prototype, "officeLocationId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunStatusResponseDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], InvoiceRunStatusResponseDto.prototype, "alsoIssue", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunStatusResponseDto.prototype, "deliveredUpTo", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], InvoiceRunStatusResponseDto.prototype, "consolidate", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], InvoiceRunStatusResponseDto.prototype, "scopeMode", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunStatusResponseDto.prototype, "startedAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], InvoiceRunStatusResponseDto.prototype, "completedAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "totalCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "successCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "skippedCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "failedCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "pendingCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], InvoiceRunStatusResponseDto.prototype, "issuedCount", void 0);
__decorate([
    Expose(),
    Type(() => InvoiceRunItemStatusDto),
    __metadata("design:type", Array)
], InvoiceRunStatusResponseDto.prototype, "items", void 0);
//# sourceMappingURL=invoice-run-status.response.dto.js.map